import {
  IonRow,
  IonCol,
  IonIcon,
  IonList,
  IonGrid,
  IonItem,
  IonLabel,
  IonBadge,
  IonTitle,
  IonHeader,
  IonButton,
  IonToolbar,
  IonButtons,
  IonContent,
  IonSegment,
  IonAccordion,
  ModalController,
  IonSegmentButton,
  IonAccordionGroup,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  InfiniteScrollCustomEvent,
} from '@ionic/angular/standalone';
import { Component, inject, input, computed, signal, linkedSignal } from '@angular/core';
import { alertCircle, personCircleOutline, chevronBack } from 'ionicons/icons';
import { asyncScheduler } from 'rxjs';
import { addIcons } from 'ionicons';
import { saveAs } from 'file-saver';
import { format } from 'date-fns';

import {
  AddressChangeAction,
  AddressChangeRecord,
  ImportAddressesResponse,
} from '@app/interfaces/swiss-post-import.interface';
import { DownloadService } from '@app/services/download.service';
import { ContactsService } from '@app/services/contacts.service';

type ImportFilter = 'all' | AddressChangeAction;

@Component({
  selector: 'app-import-addresses-result',
  templateUrl: './import-addresses-result.component.html',
  styleUrls: ['./import-addresses-result.component.scss'],
  imports: [
    IonRow,
    IonCol,
    IonList,
    IonItem,
    IonGrid,
    IonIcon,
    IonLabel,
    IonTitle,
    IonBadge,
    IonButton,
    IonHeader,
    IonButtons,
    IonToolbar,
    IonContent,
    IonSegment,
    IonAccordion,
    IonSegmentButton,
    IonAccordionGroup,
    IonInfiniteScroll,
    IonInfiniteScrollContent,
  ],
})
export class ImportAddressesResultComponent {
  readonly infiniteScrollLimit = 20;
  readonly Action = AddressChangeAction;
  modalCtrl = inject(ModalController);
  contactsSrv = inject(ContactsService);
  downloadSrv = inject(DownloadService);
  response = input.required<ImportAddressesResponse>();

  allAddresses = computed(() => this.response().changes);
  movedAddresses = computed(() => this.response().changes.filter((c) => c.action === AddressChangeAction.MOVED));
  manualReviewAddresses = computed(() =>
    this.response().changes.filter((c) => c.action === AddressChangeAction.MANUAL_REVIEW),
  );
  correctedAddresses = computed(() =>
    this.response().changes.filter((c) => c.action === AddressChangeAction.CORRECTED),
  );

  protected isLastBunchChanges = signal(false);
  protected filter = signal<ImportFilter>('all');
  protected filteredChanges = computed<AddressChangeRecord[]>(() => {
    switch (this.filter()) {
      case 'all':
        return this.allAddresses();
      case AddressChangeAction.MOVED:
        return this.movedAddresses();
      case AddressChangeAction.MANUAL_REVIEW:
        return this.manualReviewAddresses();
      case AddressChangeAction.CORRECTED:
        return this.correctedAddresses();
    }
  });
  protected listChanges = linkedSignal(() => this.filteredChanges().slice(0, this.infiniteScrollLimit));

  constructor() {
    addIcons({ chevronBack, alertCircle, personCircleOutline });
  }

  protected dismiss() {
    this.modalCtrl.dismiss();
  }

  protected setFilter(filter: string | number | undefined) {
    if (typeof filter === 'string') {
      this.filter.set(filter as ImportFilter);
      this.isLastBunchChanges.set(this.listChanges().length === this.filteredChanges().length);
    }
  }

  protected actionColor(action: AddressChangeAction): string {
    switch (action) {
      case AddressChangeAction.CORRECTED:
        return 'success';
      case AddressChangeAction.MOVED:
        return 'warning';
      case AddressChangeAction.MANUAL_REVIEW:
        return 'danger';
      default:
        return 'medium';
    }
  }

  protected actionLabel(action: AddressChangeAction): string {
    switch (action) {
      case AddressChangeAction.CORRECTED:
        return 'Corrected';
      case AddressChangeAction.MOVED:
        return 'Moved';
      case AddressChangeAction.MANUAL_REVIEW:
        return 'Manual review';
      default:
        return action;
    }
  }

  protected addressLine(address: { STREET?: string; POSTAL_CODE?: string; CITY?: string } | null): string {
    if (!address) {
      return '—';
    }
    return [address.STREET, [address.POSTAL_CODE, address.CITY].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  }

  protected openContactDetailsPage(contactId: number) {
    this.contactsSrv.showContactDetailsPage(contactId);
  }

  protected doInfinite(event: InfiniteScrollCustomEvent) {
    asyncScheduler.schedule(() => {
      this.listChanges.set(this.filteredChanges().slice(0, this.listChanges().length + this.infiniteScrollLimit));
      event.target.complete();
      this.isLastBunchChanges.set(this.listChanges().length === this.filteredChanges().length);
    }, 500);
  }

  downloadSummaryReport() {
    this.downloadSrv.convertAddressChanges(this.response().changes).subscribe((blob) => {
      const filename = `Address_Changes_Report_${format(new Date(), 'yyyyMMdd')}.xlsx`;
      saveAs(blob, filename);
    });
  }
}
